import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarCheck, FileSpreadsheet, FileText } from 'lucide-react';
import {
  BoutonPrincipal,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import {
  useCompleterDateRupture,
  useDeclarerRupture,
  urlExportRuptures,
  useRuptures,
  type ContratEnCours,
  type LigneRupture,
} from '../lib/useRuptures';
import { urlDocumentGenere } from '../lib/useDocuments';
import { ErreurApi } from '../lib/api';
import { useToast } from '../components/ui/useToast';
import { Modale } from '../components/ui/Modale';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';

function jour(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/** Jour civil réunionnais au format attendu par un `input[type=date]`. */
function jourSaisie(valeur: string): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Indian/Reunion' }).format(new Date(valeur));
}

/**
 * Écran « Ruptures de contrat » du pôle administratif (CDC §4.6).
 *
 * Deux informations suffisent à déclarer une rupture : le motif et le maintien
 * ou non de la formation. Le reste est automatique — document de rupture
 * généré, dossier basculé en recherche d'entreprise si la formation est
 * maintenue, pôles pédagogique et commercial informés.
 *
 * La date est facultative : le formulaire se prépare souvent avant qu'elle soit
 * arrêtée. Sans elle, la case reste vierge, et la date se complète ensuite
 * depuis le détail de la rupture, ce qui régénère le formulaire.
 */
export function AdmRupturesPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useRuptures(site);
  const declaration = useDeclarerRupture();
  const toast = useToast();
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [ruptureSelectionneeId, setRuptureSelectionneeId] = useState<string | null>(null);
  const [documentApercu, setDocumentApercu] = useState<string | null>(null);

  const compteurs = data?.compteurs;
  const ruptures = data?.ruptures ?? [];
  // Relue dans les données : la date complétée s'y affiche dès le rechargement.
  const ruptureSelectionnee =
    ruptures.find((ligne) => ligne.id === ruptureSelectionneeId) ?? null;
  const setRuptureSelectionnee = (ligne: LigneRupture | null) =>
    setRuptureSelectionneeId(ligne?.id ?? null);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Ruptures de contrat"
        sousTitre="Pôle administratif · document de rupture, motif, maintien ou non de la formation"
        actions={
          <>
            <button
              onClick={() => {
                if (ruptures.length === 0) {
                  toast('Aucune rupture à exporter dans le périmètre affiché');
                  return;
                }
                // Le jeton de session voyage dans un cookie httpOnly : une
                // navigation directe l'emporte, là où un fetch obligerait à
                // reconstruire le téléchargement à la main.
                window.location.href = urlExportRuptures(site);
                toast('Export du tableau des ruptures en cours');
              }}
              className="inline-flex items-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-codialis-navy px-4 py-2.5 rounded-xl text-sm font-semibold transition"
            >
              <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
              Exporter (Excel)
            </button>
            <button
              onClick={() => setFormulaireOuvert(true)}
              className="bg-codialis-navy hover:bg-codialis-deep text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition"
            >
              + Déclarer une rupture
            </button>
          </>
        }
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Ruptures enregistrées"
          valeur={compteurs?.ruptures ?? '—'}
          detail={
            compteurs
              ? `sur ${compteurs.contrats} contrat${compteurs.contrats > 1 ? 's' : ''} · ${compteurs.tauxPourcent
                  .toString()
                  .replace('.', ',')} %`
              : 'périmètre affiché'
          }
          ton="red"
        />
        <Compteur
          libelle="Formation maintenue"
          valeur={compteurs?.formationMaintenue ?? '—'}
          detail="recherche d’une nouvelle entreprise"
          ton="mint"
        />
        <Compteur
          libelle="Pendant période d’essai"
          valeur={compteurs?.pendantPeriodeEssai ?? '—'}
          detail="dans les 45 premiers jours"
          ton="warm"
        />
        <Compteur
          libelle="Délai moyen de traitement"
          valeur={
            compteurs?.delaiMoyenJours === null || compteurs?.delaiMoyenJours === undefined
              ? '—'
              : `${compteurs.delaiMoyenJours.toString().replace('.', ',')} j`
          }
          detail="objectif < 7 j"
          ton="blue"
        />
      </div>

      <div className="space-y-6">
        <div className="space-y-6">
          <Carte titre="Tableau de suivi des ruptures" sansBordure>
            {error && (
              <div className="p-4 sm:p-6">
                <MessageErreur
                  message={
                    error instanceof ErreurApi
                      ? error.message
                      : 'Le tableau des ruptures n’a pas pu être chargé.'
                  }
                />
              </div>
            )}

            {isLoading && <Chargement libelle="Chargement des ruptures…" />}

            {!isLoading && !error && (
              <div className="relative overflow-x-auto">
                <table className="w-full min-w-[820px]">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      {['Apprenant', 'Entreprise', 'Date', 'Motif', 'Formation maintenue', ''].map(
                        (colonne) => (
                          <th
                            key={colonne}
                            className="px-4 sm:px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                          >
                            {colonne || <span className="sr-only">Actions</span>}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {ruptures.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 sm:px-6 py-10 text-center text-sm text-codialis-gray"
                        >
                          Aucune rupture enregistrée dans le périmètre affiché
                        </td>
                      </tr>
                    )}
                    {ruptures.map((ligne) => (
                      <LigneTableau
                        key={ligne.id}
                        ligne={ligne}
                        onSelectionner={() => setRuptureSelectionnee(ligne)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Carte>

          {data && data.motifs.length > 0 && (
            <Carte titre="Répartition par motif" sansBordure>
              <div className="space-y-3 p-4 sm:p-6">
                {data.motifs.map((entree) => (
                  <div key={entree.motif}>
                    <div className="flex items-baseline justify-between gap-4 text-sm">
                      <span className="text-codialis-navy">{entree.motif}</span>
                      <span className="font-semibold text-codialis-gray">{entree.nombre}</span>
                    </div>
                    <div className="progress-track mt-1">
                      <div
                        className="progress-bar"
                        style={{
                          width: `${Math.round((entree.nombre / Math.max(1, ruptures.length)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Carte>
          )}
        </div>
      </div>

      <Modale
        ouverte={formulaireOuvert}
        onFermer={() => setFormulaireOuvert(false)}
        titre="Déclarer une rupture"
        sousTitre="Document de rupture, motif, maintien ou non de la formation"
        largeur="max-w-xl"
      >
        <FormulaireRupture
          contrats={data?.contratsEnCours ?? []}
          motifs={data?.motifsDisponibles ?? []}
          enCours={declaration.isPending}
          erreur={
            declaration.error instanceof ErreurApi ? declaration.error.message : null
          }
          resultat={declaration.data ?? null}
          onDeclarer={(valeurs) => declaration.mutate(valeurs)}
          onPrevisualiserDocument={setDocumentApercu}
        />
      </Modale>

      {ruptureSelectionnee && (
        <Modale
          ouverte={Boolean(ruptureSelectionnee)}
          onFermer={() => setRuptureSelectionnee(null)}
          titre={`Rupture · ${ruptureSelectionnee.apprenant}`}
          sousTitre={`Antenne ${ruptureSelectionnee.siteCode} · Informations liées à la rupture de contrat`}
          largeur="max-w-2xl"
        >
          <div className="space-y-6 p-4 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                  Apprenant
                </div>
                <div className="mt-1 font-semibold text-codialis-navy">
                  {ruptureSelectionnee.apprenant}
                </div>
                <div className="mt-0.5 text-xs text-codialis-gray">
                  {ruptureSelectionnee.formation ?? 'Formation non affectée'}
                </div>
              </div>

              <div className="rounded-xl bg-gray-50 p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                  Entreprise
                </div>
                <div className="mt-1 font-semibold text-codialis-navy">
                  {ruptureSelectionnee.entreprise}
                </div>
                <div className="mt-0.5 text-xs text-codialis-gray">
                  Antenne {ruptureSelectionnee.siteCode}
                </div>
              </div>

              <div className="rounded-xl bg-gray-50 p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                  Date de rupture
                </div>
                {ruptureSelectionnee.dateRupture ? (
                  <div className="mt-1 font-semibold text-codialis-navy">
                    {jour(ruptureSelectionnee.dateRupture)}
                  </div>
                ) : (
                  <div className="mt-1.5">
                    <Pastille texte="À préciser" ton="warning" />
                  </div>
                )}
                {ruptureSelectionnee.pendantPeriodeEssai && (
                  <div className="mt-1.5">
                    <Pastille texte="Période d’essai" ton="warning" />
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-gray-50 p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                  Maintien de la formation
                </div>
                <div className="mt-1.5">
                  <Pastille
                    texte={ruptureSelectionnee.formationMaintenue ? 'Oui · Formation maintenue' : 'Non · Arrêt du parcours'}
                    ton={ruptureSelectionnee.formationMaintenue ? 'success' : 'danger'}
                  />
                </div>
                <div className="mt-1 text-xs text-codialis-gray">
                  {ruptureSelectionnee.formationMaintenue
                    ? 'Recherche d’une nouvelle entreprise'
                    : 'Fin définitive de la formation'}
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-gray-50 p-4">
              <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                Motif
              </div>
              <div className="mt-1 text-sm font-medium text-codialis-navy">
                {ruptureSelectionnee.motif}
              </div>
            </div>

            {!ruptureSelectionnee.dateRupture && (
              <CompleterDate
                key={ruptureSelectionnee.id}
                rupture={ruptureSelectionnee}
                onPrevisualiserDocument={setDocumentApercu}
              />
            )}

            {ruptureSelectionnee.commentaire && (
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
                  Commentaire / Précisions
                </div>
                <div className="mt-1 text-sm text-codialis-navy whitespace-pre-wrap">
                  {ruptureSelectionnee.commentaire}
                </div>
              </div>
            )}

            {ruptureSelectionnee.dateDebutContrat && (
              <div className="rounded-xl border border-gray-100 bg-codialis-sand/40 p-3 text-xs text-codialis-gray">
                Contrat initial : début le {jour(ruptureSelectionnee.dateDebutContrat)}
                {ruptureSelectionnee.dateFinContrat ? ` · fin prévue le ${jour(ruptureSelectionnee.dateFinContrat)}` : ''}
              </div>
            )}

            {/* Le document peut manquer sur une rupture saisie avant que le
                formulaire officiel ne soit installé : on le dit, plutôt que
                d'offrir un bouton qui mènerait à un 404. */}
            {ruptureSelectionnee.documentId ? (
              <button
                type="button"
                onClick={() => setDocumentApercu(ruptureSelectionnee.documentId)}
                className="inline-flex items-center gap-2 rounded-xl border border-codialis-navy/20 bg-codialis-sand px-4 py-3 text-sm font-semibold text-codialis-navy transition hover:bg-codialis-navy hover:text-white focus:outline-none focus:ring-2 focus:ring-codialis-sky"
              >
                <FileText className="h-4 w-4" aria-hidden="true" />
                Prévisualiser le formulaire de rupture
              </button>
            ) : (
              <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-codialis-gray">
                Aucun document de rupture rattaché à cette déclaration.
              </div>
            )}

            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4">
              <button
                type="button"
                onClick={() => setRuptureSelectionnee(null)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-codialis-navy hover:bg-gray-50 transition"
              >
                Fermer
              </button>
              <Link
                to={`/adm-documents/${ruptureSelectionnee.apprenantId}`}
                className="rounded-xl border border-codialis-navy px-4 py-2.5 text-sm font-semibold text-codialis-navy hover:bg-gray-50 transition"
              >
                Documents réglementaires
              </Link>
              <Link
                to={`/adm-dossier/${ruptureSelectionnee.apprenantId}`}
                className="rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-codialis-deep transition"
              >
                Consulter le dossier
              </Link>
            </div>
          </div>
        </Modale>
      )}

      {documentApercu && (
        <VisionneusePiece
          ouverte
          libellePiece="Document de rupture"
          nomFichier="rupture-contrat.pdf"
          urlBase={urlDocumentGenere(documentApercu)}
          onFermer={() => setDocumentApercu(null)}
        />
      )}
    </div>
  );
}

function LigneTableau({
  ligne,
  onSelectionner,
}: {
  ligne: LigneRupture;
  onSelectionner: () => void;
}): JSX.Element {
  return (
    <tr
      onClick={onSelectionner}
      className="cursor-pointer transition hover:bg-gray-50"
    >
      <td className="px-4 sm:px-6 py-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelectionner();
          }}
          className="text-left font-semibold text-codialis-navy hover:text-codialis-sky hover:underline"
        >
          {ligne.apprenant}
        </button>
        <div className="text-[11px] text-codialis-gray">{ligne.formation ?? 'Formation non affectée'}</div>
      </td>
      <td className="px-4 py-3 text-sm text-codialis-gray">{ligne.entreprise}</td>
      <td className="px-4 py-3 text-xs text-codialis-gray">
        {ligne.dateRupture ? (
          <span className="font-mono">{jour(ligne.dateRupture)}</span>
        ) : (
          <Pastille texte="À préciser" ton="warning" />
        )}
      </td>
      <td className="px-4 py-3 text-xs text-codialis-gray">
        {ligne.motif}
        {ligne.pendantPeriodeEssai && (
          <span className="ml-2">
            <Pastille texte="Période d’essai" ton="warning" />
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <Pastille
          texte={ligne.formationMaintenue ? 'Oui' : 'Non'}
          ton={ligne.formationMaintenue ? 'success' : 'danger'}
        />
      </td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelectionner();
          }}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-100"
        >
          Détails
        </button>
      </td>
    </tr>
  );
}

interface ValeursDeclaration {
  apprenantId: string;
  dateRupture?: string;
  motif: string;
  formationMaintenue: boolean;
  commentaire?: string;
}

function FormulaireRupture({
  contrats,
  motifs,
  enCours,
  erreur,
  resultat,
  onDeclarer,
  onPrevisualiserDocument,
}: {
  contrats: ContratEnCours[];
  motifs: string[];
  enCours: boolean;
  erreur: string | null;
  resultat: {
    documentId: string;
    formationMaintenue: boolean;
    informes: number;
    avertissements: string[];
  } | null;
  onDeclarer: (valeurs: ValeursDeclaration) => void;
  onPrevisualiserDocument: (documentId: string) => void;
}): JSX.Element {
  const [apprenantId, setApprenantId] = useState('');
  // Vide par défaut : une date supposée finirait sur un document officiel.
  const [dateRupture, setDateRupture] = useState('');
  const [motif, setMotif] = useState('');
  const [formationMaintenue, setFormationMaintenue] = useState(false);
  const [commentaire, setCommentaire] = useState('');

  const contratChoisi = useMemo(
    () => contrats.find((contrat) => contrat.apprenantId === apprenantId) ?? null,
    [contrats, apprenantId],
  );

  const motifRetenu = motif || motifs[0] || '';
  const complet = Boolean(apprenantId && motifRetenu);

  return (
    <div className="bg-white">
      <form
        className="space-y-4 p-4 sm:p-6"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (!complet || enCours) return;
          onDeclarer({
            apprenantId,
            ...(dateRupture ? { dateRupture } : {}),
            motif: motifRetenu,
            formationMaintenue,
            ...(commentaire.trim() ? { commentaire: commentaire.trim() } : {}),
          });
        }}
      >
        <Champ libelle="Apprenant">
          <select
            value={apprenantId}
            onChange={(evenement) => setApprenantId(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          >
            <option value="" disabled>Choisir un contrat en cours…</option>
            {contrats.map((contrat) => (
              <option key={contrat.apprenantId} value={contrat.apprenantId}>
                [{contrat.siteCode}] {contrat.apprenant} · {contrat.entreprise}
              </option>
            ))}
          </select>
          {contrats.length === 0 && (
            <p className="mt-1 text-[11px] text-codialis-gray">
              Aucun contrat en cours dans le périmètre affiché.
            </p>
          )}
          {contratChoisi && (
            <p className="mt-1 text-[11px] text-codialis-gray">
              Contrat du {jour(contratChoisi.dateDebut)}
              {contratChoisi.finPeriodeEssai
                ? ` · période d’essai jusqu’au ${jour(contratChoisi.finPeriodeEssai)}`
                : ''}
            </p>
          )}
        </Champ>

        <Champ libelle="Date de rupture (facultative)">
          <input
            type="date"
            value={dateRupture}
            onChange={(evenement) => setDateRupture(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          />
          <p className="mt-1 text-[11px] text-codialis-gray">
            Laisser vide si elle n’est pas encore arrêtée : la case reste vierge sur le formulaire,
            et la date se complète ensuite depuis le détail de la rupture.
          </p>
        </Champ>

        <Champ libelle="Motif">
          <select
            value={motifRetenu}
            onChange={(evenement) => setMotif(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
          >
            {motifs.map((valeur) => (
              <option key={valeur} value={valeur}>
                {valeur}
              </option>
            ))}
          </select>
        </Champ>

        <Champ libelle="Maintien de la formation" groupe>
          <div className="flex gap-2">
            {[true, false].map((valeur) => (
              <label
                key={String(valeur)}
                className={`flex min-w-0 flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border py-2.5 text-sm transition ${
                  formationMaintenue === valeur
                    ? 'border-codialis-mint bg-codialis-sand font-semibold text-codialis-navy'
                    : 'border-gray-200 bg-gray-50 text-codialis-gray'
                }`}
              >
                <input
                  type="radio"
                  name="maintien"
                  checked={formationMaintenue === valeur}
                  onChange={() => setFormationMaintenue(valeur)}
                />
                {valeur ? 'Oui' : 'Non'}
              </label>
            ))}
          </div>
        </Champ>

        <Champ libelle="Commentaire (facultatif)">
          <textarea
            value={commentaire}
            onChange={(evenement) => setCommentaire(evenement.target.value)}
            rows={3}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
            placeholder="Précision portée au document de rupture"
          />
        </Champ>

        {erreur && <MessageErreur message={erreur} />}

        {resultat && (
          <div className="space-y-2 rounded-xl bg-codialis-sand p-4 text-xs text-codialis-navy">
            <p>
              Rupture enregistrée · document généré · {resultat.informes} personne
              {resultat.informes > 1 ? 's' : ''} informée{resultat.informes > 1 ? 's' : ''} aux
              pôles pédagogique et commercial.
              {resultat.formationMaintenue &&
                ' Le dossier est passé en recherche d’entreprise.'}
            </p>
            <button
              type="button"
              onClick={() => onPrevisualiserDocument(resultat.documentId)}
              className="inline-flex items-center gap-2 rounded-lg border border-codialis-navy/20 bg-white px-3 py-2 text-xs font-medium text-codialis-navy hover:bg-codialis-navy hover:text-white"
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              Prévisualiser le formulaire de rupture
            </button>
            {resultat.avertissements.length > 0 && (
              <div>
                <Pastille texte="À finir à la main" ton="warning" />
                <ul className="mt-1 list-inside list-disc">
                  {resultat.avertissements.map((avertissement) => (
                    <li key={avertissement}>{avertissement}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <BoutonPrincipal type="submit" disabled={!complet || enCours}>
            {enCours ? 'Enregistrement…' : 'Enregistrer la rupture'}
          </BoutonPrincipal>
        </div>

        <p className="text-[11px] text-codialis-gray">
          Si la formation est maintenue, le dossier bascule automatiquement dans la vue
          « apprenants sans entreprise » du pôle pédagogique.
        </p>
      </form>
    </div>
  );
}

function Champ({
  libelle,
  children,
  groupe = false,
}: {
  libelle: string;
  children: React.ReactNode;
  /** Plusieurs commandes (boutons, choix) : un `fieldset`, le libellé en légende. */
  groupe?: boolean;
}): JSX.Element {
  // Le libellé enveloppe son champ : il le nomme sans identifiant à poser
  // (RGAA 11.1, TF-341).
  if (groupe) {
    return (
      <fieldset>
        <legend className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
          {libelle}
        </legend>
        {children}
      </fieldset>
    );
  }
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
      </span>
      {children}
    </label>
  );
}

/**
 * Date d'une rupture déclarée sans elle. L'enregistrer régénère le formulaire,
 * sauf s'il est déjà signé ou en signature : il est alors conservé, et l'écran
 * le dit plutôt que de laisser croire que le document porte la date.
 */
function CompleterDate({
  rupture,
  onPrevisualiserDocument,
}: {
  rupture: LigneRupture;
  onPrevisualiserDocument: (documentId: string) => void;
}): JSX.Element {
  const completion = useCompleterDateRupture();
  const toast = useToast();
  const [dateRupture, setDateRupture] = useState('');
  const debut = rupture.dateDebutContrat ? jourSaisie(rupture.dateDebutContrat) : undefined;

  return (
    <form
      className="space-y-3 rounded-xl border border-codialis-mint/60 bg-codialis-sand p-4"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (!dateRupture || completion.isPending) return;
        completion.mutate(
          { ruptureId: rupture.id, apprenantId: rupture.apprenantId, dateRupture },
          {
            onSuccess: (resultat) => {
              toast(
                resultat.formulaireRegenere
                  ? 'Date enregistrée · formulaire de rupture régénéré'
                  : 'Date enregistrée · formulaire signé conservé tel quel',
              );
              if (resultat.documentId && resultat.formulaireRegenere) {
                onPrevisualiserDocument(resultat.documentId);
              }
            },
          },
        );
      }}
    >
      <div>
        <label
          htmlFor={`date-rupture-${rupture.id}`}
          className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy"
        >
          Compléter la date de rupture
        </label>
        <p className="mb-2 text-[11px] text-codialis-gray">
          Le formulaire de rupture est régénéré avec cette date. Un formulaire déjà signé est
          conservé tel quel : la date s’y porte à la main.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id={`date-rupture-${rupture.id}`}
            type="date"
            value={dateRupture}
            min={debut}
            onChange={(evenement) => setDateRupture(evenement.target.value)}
            className="w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm sm:flex-1"
          />
          <BoutonPrincipal type="submit" disabled={!dateRupture || completion.isPending}>
            <CalendarCheck className="mr-2 h-4 w-4" aria-hidden="true" />
            {completion.isPending ? 'Enregistrement…' : 'Enregistrer la date'}
          </BoutonPrincipal>
        </div>
      </div>
      {completion.error && (
        <MessageErreur
          message={
            completion.error instanceof ErreurApi
              ? completion.error.message
              : 'La date n’a pas pu être enregistrée.'
          }
        />
      )}
    </form>
  );
}
